import type { MultilingualQuestion } from '../../types/multilingualQuestion';

// Multilingual version — traffic-lights-signals-2 — CBR 2026 format
export const trafficLightsSignals2Multilingual: MultilingualQuestion[] = [
  {
    id: 'q-tls2-1',
    animationId: null,
    text: {
      en: 'The traffic light has been green for a long time. You are approaching at 50 km/h. What should you keep in mind?',
      nl: 'Het verkeerslicht is al lang groen. U nadert met 50 km/u. Waarmee moet u rekening houden?',
      ar: 'أصبحت إشارة المرور خضراء منذ وقت طويل. أنت تقترب بسرعة 50 كم/س. ماذا يجب أن تضع في اعتبارك؟',
    },
    options: [
      {
        id: 'q-tls2-1o1',
        text: {
          en: 'Speed up to pass before it changes',
          nl: 'Versnellen om erdoorheen te rijden voordat het verandert',
          ar: 'زد السرعة لتمر قبل أن تتغير',
        },
      },
      {
        id: 'q-tls2-1o2',
        text: {
          en: 'Be prepared for it to change to amber; be ready to stop safely',
          nl: 'Wees erop voorbereid dat het oranje wordt; wees klaar om veilig te stoppen',
          ar: 'كن مستعدا لأن تتحول إلى الكهرماني؛ كن مستعدا للتوقف بأمان',
        },
      },
      {
        id: 'q-tls2-1o3',
        text: {
          en: 'Green means go; no need to slow down',
          nl: 'Groen betekent doorrijden; geen reden om te vertragen',
          ar: 'الأخضر يعني المضي؛ لا حاجة للإبطاء',
        },
      },
    ],
    correctAnswerId: 'q-tls2-1o2',
    explanation: {
      en: 'A light that has been green for a long time may change soon. Be prepared to stop safely if it turns amber.',
      nl: 'Een licht dat al lang groen is, kan binnenkort veranderen. Wees voorbereid om veilig te stoppen als het oranje wordt.',
      ar: 'قد يتغير الضوء الأخضر الذي بقي طويلا قريبا. كن مستعدا للتوقف بأمان إذا تحول إلى الكهرماني.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-2',
    animationId: null,
    text: {
      en: 'You are at a junction. The traffic light is red. A green arrow for turning left is also lit. What may you do?',
      nl: 'U bevindt zich op een kruising. Het verkeerslicht is rood. Er brandt ook een groene pijl voor linksaf. Wat mag u doen?',
      ar: 'أنت عند تقاطع. إشارة المرور حمراء. يضيء أيضا سهم أخضر للانعطاف يسارا. ماذا يجوز لك أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-2o1',
        text: {
          en: 'You may turn left, giving way to cyclists and pedestrians crossing your path',
          nl: 'U mag linksaf, voorrang verlenend aan fietsers en voetgangers die uw pad kruisen',
          ar: 'يجوز لك الانعطاف يسارا مع إعطاء الأولوية للدراجين والمشاة الذين يعبرون مسارك',
        },
      },
      {
        id: 'q-tls2-2o2',
        text: {
          en: 'You must wait for the full green light',
          nl: 'U moet wachten op het volledige groene licht',
          ar: 'يجب عليك انتظار الضوء الأخضر الكامل',
        },
      },
      {
        id: 'q-tls2-2o3',
        text: {
          en: 'You may turn left with full priority over all other road users',
          nl: 'U mag linksaf met volledige voorrang op alle andere weggebruikers',
          ar: 'يجوز لك الانعطاف يسارا بأولوية كاملة على جميع مستخدمي الطريق',
        },
      },
    ],
    correctAnswerId: 'q-tls2-2o1',
    explanation: {
      en: 'A green filter arrow allows you to proceed in that direction despite the red main light. You must still give way to cyclists and pedestrians crossing your path.',
      nl: 'Een groene filterpijl staat u toe in die richting te rijden ondanks het rode hoofdlicht. U moet fietsers en voetgangers die uw pad kruisen altijd voorrang geven.',
      ar: 'السهم الأخضر المرشح يسمح لك بالمضي في ذلك الاتجاه رغم الضوء الأحمر الرئيسي. يجب أن تمنح الأولوية للدراجين والمشاة الذين يعبرون مسارك.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-3',
    animationId: null,
    text: {
      en: 'At a pedestrian crossing with traffic lights, the light is green for you but a pedestrian has just stepped onto the crossing. What must you do?',
      nl: 'Bij een voetgangersoversteekplaats met verkeerslichten is het licht voor u groen, maar een voetganger is net de oversteek op gegaan. Wat moet u doen?',
      ar: 'عند معبر مشاة مع إشارات مرور، ضوءك أخضر لكن أحد المشاة خطا للتو على المعبر. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-3o1',
        text: {
          en: 'Proceed; the light is green',
          nl: 'Doorrijden; het licht is groen',
          ar: 'تابع؛ الضوء أخضر',
        },
      },
      {
        id: 'q-tls2-3o2',
        text: {
          en: 'Give way to the pedestrian; they are on the crossing',
          nl: 'Voorrang verlenen aan de voetganger; die staat op de oversteek',
          ar: 'أعط الأولوية للمشاة؛ هم على المعبر',
        },
      },
      {
        id: 'q-tls2-3o3',
        text: {
          en: 'Sound your horn to warn the pedestrian',
          nl: 'Claxonneren om de voetganger te waarschuwen',
          ar: 'استخدم البوق لتحذير المشاة',
        },
      },
    ],
    correctAnswerId: 'q-tls2-3o2',
    explanation: {
      en: 'You must give way to pedestrians who are already on the crossing, even if your light is green.',
      nl: 'U moet voorrang verlenen aan voetgangers die al op de oversteek zijn, ook als uw licht groen is.',
      ar: 'يجب أن تمنح الأولوية للمشاة الذين هم بالفعل على المعبر، حتى لو كان ضوءك أخضر.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-4',
    animationId: null,
    text: {
      en: 'You approach a junction. The main light is red but a green arrow points left. A pedestrian is crossing from the left side of your path. What do you do?',
      nl: 'U nadert een kruising. Het hoofdlicht is rood maar een groene pijl wijst naar links. Een voetganger steekt over van links voor u. Wat doet u?',
      ar: 'تقترب من تقاطع. الضوء الرئيسي أحمر لكن سهما أخضر يشير يسارا. أحد المشاة يعبر من اليسار أمامك. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-4o1',
        text: {
          en: 'Stop — the main light is red',
          nl: 'Stoppen — het hoofdlicht is rood',
          ar: 'توقف — الضوء الرئيسي أحمر',
        },
      },
      {
        id: 'q-tls2-4o2',
        text: {
          en: 'Turn left, giving way to the pedestrian crossing',
          nl: 'Linksaf slaan en de overstekende voetganger voorrang verlenen',
          ar: 'انعطف يسارا مع إعطاء الأولوية للمشاة الذين يعبرون',
        },
      },
      {
        id: 'q-tls2-4o3',
        text: {
          en: 'Turn left — the green arrow gives priority over pedestrians',
          nl: 'Linksaf slaan — de groene pijl geeft voorrang op voetgangers',
          ar: 'انعطف يسارا — السهم الأخضر يمنحك الأولوية على المشاة',
        },
      },
    ],
    correctAnswerId: 'q-tls2-4o2',
    explanation: {
      en: 'A green filter arrow allows you to turn in its direction despite the red main light. You must still give way to pedestrians and cyclists crossing your path.',
      nl: 'Een groene filterpijl staat u toe die richting op te rijden ondanks het rode hoofdlicht. U moet voetgangers en fietsers die uw pad kruisen altijd voorrang geven.',
      ar: 'يسمح لك السهم الأخضر المرشح بالانعطاف في اتجاهه رغم الضوء الأحمر الرئيسي. يجب عليك دائما منح الأولوية للمشاة والدراجين الذين يعبرون مسارك.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-5',
    animationId: null,
    text: {
      en: 'Twin red lights are flashing at a level crossing. No train is visible in either direction. What must you do?',
      nl: 'Dubbele rode lichten knipperen bij een spoorwegovergang. Er is geen trein zichtbaar in beide richtingen. Wat moet u doen?',
      ar: 'أضواء حمراء مزدوجة تومض عند معبر سكة حديد. لا يوجد قطار مرئي في أي اتجاه. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-5o1',
        text: {
          en: 'Cross quickly — no train is coming',
          nl: 'Snel oversteken — er komt geen trein',
          ar: 'اعبر بسرعة — لا يوجد قطار قادم',
        },
      },
      {
        id: 'q-tls2-5o2',
        text: {
          en: 'Stop and wait — flashing red means a train is approaching regardless of visibility',
          nl: 'Stoppen en wachten — knipperend rood betekent dat een trein nadert ongeacht de zichtbaarheid',
          ar: 'توقف وانتظر — الأحمر الوامض يعني اقتراب قطار بغض النظر عن الرؤية',
        },
      },
      {
        id: 'q-tls2-5o3',
        text: {
          en: 'Slow down and cross carefully',
          nl: 'Vertragen en voorzichtig oversteken',
          ar: 'أبطئ واعبر بحذر',
        },
      },
    ],
    correctAnswerId: 'q-tls2-5o2',
    explanation: {
      en: 'Flashing red at a level crossing always means stop — even if no train is visible. Trains can approach fast from out of sight. Wait until both lights stop and any barriers are fully raised.',
      nl: 'Knipperend rood bij een spoorwegovergang betekent altijd stoppen — ook als er geen trein zichtbaar is. Treinen kunnen snel naderen vanuit het zicht. Wacht tot de lichten stoppen en de slagbomen volledig omhoog zijn.',
      ar: 'الأحمر الوامض عند معبر السكة يعني دائما التوقف — حتى لو لم يكن أي قطار مرئيا. القطارات يمكن أن تقترب بسرعة من خارج نطاق الرؤية. انتظر حتى تتوقف الأضواء وترتفع الحواجز بالكامل.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-6',
    animationId: null,
    text: {
      en: 'You are driving behind a tram at a junction. Its signal shows a steady white horizontal bar. The tram slows. A gap opens to its right. What do you do?',
      nl: 'U rijdt achter een tram bij een kruising. Zijn sein toont een vaste witte horizontale balk. De tram vertraagt. Rechts ontstaat een opening. Wat doet u?',
      ar: 'أنت تسير خلف ترام عند تقاطع. إشارته تظهر شريطا أبيضا أفقيا ثابتا. الترام يبطئ. تظهر فجوة على يمينه. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-6o1',
        text: {
          en: 'Pass the tram through the gap on the right',
          nl: 'De tram via de opening rechts inhalen',
          ar: 'تجاوز الترام عبر الفجوة من اليمين',
        },
      },
      {
        id: 'q-tls2-6o2',
        text: {
          en: 'Stay behind the tram — it must stop, the gap may close suddenly',
          nl: 'Achter de tram blijven — hij moet stoppen en de opening kan plotseling sluiten',
          ar: 'ابق خلف الترام — يجب أن يتوقف، والفجوة قد تغلق فجأة',
        },
      },
      {
        id: 'q-tls2-6o3',
        text: {
          en: 'The white bar means go — the tram is making way for you',
          nl: 'De witte balk betekent rijden — de tram maakt ruimte voor u',
          ar: 'الشريط الأبيض يعني المضي — الترام يفسح لك المجال',
        },
      },
    ],
    correctAnswerId: 'q-tls2-6o2',
    explanation: {
      en: 'A white horizontal bar means the tram must stop. Never pass a braking tram on the right — cyclists or pedestrians may be hidden in the gap.',
      nl: 'Een witte horizontale balk betekent dat de tram moet stoppen. Haal een remmende tram nooit rechts in — er kunnen fietsers of voetgangers in die opening schuilen.',
      ar: 'الشريط الأبيض الأفقي يعني أن الترام يجب أن يتوقف. لا تتجاوز تراما يكبح من اليمين أبدا — قد يختبئ دراجون أو مشاة في تلك الفجوة.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-7',
    animationId: null,
    text: {
      en: 'You are stopped at a red light. It changes to red and amber together. The driver behind you starts honking. What do you do?',
      nl: 'U wacht bij rood. Het licht springt op rood en oranje tegelijk. De bestuurder achter u begint te toeteren. Wat doet u?',
      ar: 'تنتظر عند الأحمر. يتحول الضوء إلى أحمر وكهرماني معا. السائق خلفك يبدأ بالتبويق. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-7o1',
        text: {
          en: 'Ignore the horn, stay ready, move only when fully green',
          nl: 'Het getoeter negeren, klaar blijven, alleen rijden bij volledig groen',
          ar: 'تجاهل البوق، ابق مستعدا، انطلق فقط عند الأخضر الكامل',
        },
      },
      {
        id: 'q-tls2-7o2',
        text: {
          en: 'Move off now — red and amber means go',
          nl: 'Nu optrekken — rood en oranje betekent rijden',
          ar: 'انطلق الآن — الأحمر والكهرماني يعني السير',
        },
      },
      {
        id: 'q-tls2-7o3',
        text: {
          en: 'Stay completely still — the lights may be faulty',
          nl: 'Volledig stilblijven — de lichten kunnen defect zijn',
          ar: 'ابق ثابتا تماما — الأضواء قد تكون معطلة',
        },
      },
    ],
    correctAnswerId: 'q-tls2-7o1',
    explanation: {
      en: 'Red and amber means prepare — not go. Wait for full green. An impatient driver behind has no right to rush you. Moving early is not permitted.',
      nl: 'Rood en oranje betekent voorbereiden — niet rijden. Wacht op volledig groen. Een ongeduldig bestuurder achter u heeft geen recht u te haasten. Vroeg rijden is niet toegestaan.',
      ar: 'الأحمر والكهرماني يعني الاستعداد — وليس السير. انتظر الأخضر الكامل. السائق غير الصبور خلفك لا يحق له استعجالك. الانطلاق المبكر غير مسموح.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-8',
    animationId: null,
    text: {
      en: 'You arrive at a junction where all traffic lights are dark. A car approaches from your right on an equal road. No signs are visible. What do you do?',
      nl: 'U komt aan bij een kruising waar alle verkeerslichten donker zijn. Een auto nadert van rechts op een gelijkwaardige weg. Er zijn geen borden. Wat doet u?',
      ar: 'تصل إلى تقاطع حيث جميع إشارات المرور مطفأة. سيارة تقترب من يمينك على طريق مماثل. لا توجد علامات. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-8o1',
        text: {
          en: 'Proceed — you arrived at the junction first',
          nl: 'Doorrijden — u was er als eerste',
          ar: 'تابع — لقد وصلت إلى التقاطع أولا',
        },
      },
      {
        id: 'q-tls2-8o2',
        text: {
          en: 'Give way to the car from the right — apply priority-to-the-right',
          nl: 'De auto van rechts voorrang verlenen — voorrang van rechts toepassen',
          ar: 'أعط الأولوية للسيارة من اليمين — طبق قاعدة أولوية اليمين',
        },
      },
      {
        id: 'q-tls2-8o3',
        text: {
          en: 'Stop and wait indefinitely; the junction is out of use',
          nl: 'Stoppen en onbepaald wachten; de kruising is buiten gebruik',
          ar: 'توقف وانتظر إلى أجل غير مسمى؛ التقاطع خارج الخدمة',
        },
      },
    ],
    correctAnswerId: 'q-tls2-8o2',
    explanation: {
      en: 'Dark lights mean revert to default rules. On equal roads: rechts heeft voorrang — give way to the right. Keep moving safely, do not wait indefinitely.',
      nl: 'Donkere lichten betekenen terugvallen op de standaardregels. Op gelijkwaardige wegen: rechts heeft voorrang. Rijd veilig door — wacht niet onbepaald.',
      ar: 'الإشارات المطفأة تعني الرجوع إلى القواعد الاعتيادية. على الطرق المتكافئة: الأولوية لليمين. تابع بأمان — لا تنتظر إلى أجل غير مسمى.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-9',
    animationId: null,
    text: {
      en: 'Your light turns green at a junction. A separate green cycle signal is also lit. You want to turn right. What must you check before turning?',
      nl: 'Uw licht wordt groen bij een kruising. Er brandt ook een groen fietssignaal. U wilt rechtsaf. Wat controleert u voor het afslaan?',
      ar: 'إشارتك تتحول إلى الأخضر عند تقاطع. إشارة دراجات خضراء منفصلة مضاءة أيضا. تريد الانعطاف يمينا. ماذا يجب أن تتحقق قبل الانعطاف؟',
    },
    options: [
      {
        id: 'q-tls2-9o1',
        text: {
          en: 'Nothing — your green gives you priority over cyclists',
          nl: 'Niets — uw groen geeft u voorrang op fietsers',
          ar: 'لا شيء — ضوءك الأخضر يمنحك الأولوية على الدراجين',
        },
      },
      {
        id: 'q-tls2-9o2',
        text: {
          en: 'Check mirrors and blind spot for cyclists crossing your path',
          nl: 'Spiegels en dode hoek controleren op fietsers die uw pad kruisen',
          ar: 'تحقق من المرايا والنقطة العمياء بحثا عن دراجين يعبرون مسارك',
        },
      },
      {
        id: 'q-tls2-9o3',
        text: {
          en: 'Wait for the cycle signal to turn red before turning',
          nl: 'Wachten tot het fietssignaal rood is voor het afslaan',
          ar: 'انتظر حتى تتحول إشارة الدراجات إلى الأحمر قبل الانعطاف',
        },
      },
    ],
    correctAnswerId: 'q-tls2-9o2',
    explanation: {
      en: 'A green cycle signal means cyclists are actively crossing. Always check mirrors and blind spot before turning right. Your green does not remove the duty to check.',
      nl: 'Een groen fietssignaal betekent dat fietsers oversteken. Controleer altijd spiegels en dode hoek voor rechtsaf slaan. Uw groen heft de kijkplicht niet op.',
      ar: 'إشارة الدراجات الخضراء تعني أن الدراجين يعبرون. تحقق دائما من المرايا والنقطة العمياء قبل الانعطاف يمينا. ضوءك الأخضر لا يلغي واجب التحقق.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-10',
    animationId: null,
    text: {
      en: 'You have stopped at a red light. An emergency vehicle with flashing blue lights and siren is behind you. What should you do?',
      nl: 'U staat stil bij rood licht. Achter u komt een hulpdienst met zwaailicht en sirene. Wat moet u doen?',
      ar: 'توقفت عند الضوء الأحمر. مركبة طوارئ خلفك بأضواء زرقاء ومستعرة وصفارة إنذار. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-10o1',
        text: {
          en: 'Stay where you are; do not cross the red light',
          nl: 'Blijf staan; rijd niet door rood',
          ar: 'ابق مكانك؛ لا تعبر الضوء الأحمر',
        },
      },
      {
        id: 'q-tls2-10o2',
        text: {
          en: 'If safe and necessary, move forward (e.g. into the junction) to let it pass, then stop',
          nl: 'Indien veilig en nodig, vooruit rijden (bijv. de kruising op) om ruimte te maken, daarna stoppen',
          ar: 'إن كان ذلك آمنا ولزاما، تقدم (مثلا إلى التقاطع) ليمر، ثم توقف',
        },
      },
      {
        id: 'q-tls2-10o3',
        text: {
          en: 'Reverse to make room',
          nl: 'Achteruit rijden om ruimte te maken',
          ar: 'ارجع للخلف لإفساح المجال',
        },
      },
    ],
    correctAnswerId: 'q-tls2-10o2',
    explanation: {
      en: 'You must give way to emergency vehicles. If you are at a red light, you may move forward carefully to let them pass if it is safe and you do not endanger others.',
      nl: 'U moet voorrang verlenen aan hulpdiensten. Bij rood licht mag u voorzichtig vooruitrijden om ze door te laten als het veilig is en u anderen niet in gevaar brengt.',
      ar: 'يجب أن تمنح الأولوية لمركبات الطوارئ. عند الضوء الأحمر، يجوز لك التقدم بحذر لإفساح المجال إذا كان ذلك آمنا ودون تعريض الآخرين للخطر.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-11',
    animationId: null,
    text: {
      en: 'At a pedestrian crossing with lights, the pedestrian light has just turned red. A pedestrian is still crossing slowly. Your light turns green. May you go?',
      nl: 'Bij een voetgangerslicht is het voetgangerslicht net rood geworden. Een voetganger steekt nog langzaam over. Uw licht wordt groen. Mag u rijden?',
      ar: 'عند معبر مشاة مع إشارات، تحول ضوء المشاة للتو إلى أحمر. أحد المشاة لا يزال يعبر ببطء. يتحول ضوءك إلى أخضر. هل يجوز لك المضي؟',
    },
    options: [
      {
        id: 'q-tls2-11o1',
        text: {
          en: 'Yes; the pedestrian had their turn',
          nl: 'Ja; de voetganger was aan de beurt',
          ar: 'نعم؛ كان دور المشاة',
        },
      },
      {
        id: 'q-tls2-11o2',
        text: {
          en: 'Only when the crossing is clear; you must not endanger anyone still on it',
          nl: 'Als de oversteek vrij is; u mag niemand op de oversteek in gevaar brengen',
          ar: 'فقط عندما يكون المعبر خاليا؛ لا تعرض من لا يزال عليه للخطر',
        },
      },
      {
        id: 'q-tls2-11o3',
        text: {
          en: 'Sound your horn so they hurry',
          nl: 'Claxonneren zodat ze opschieten',
          ar: 'استخدم البوق ليسرعوا',
        },
      },
    ],
    correctAnswerId: 'q-tls2-11o2',
    explanation: {
      en: 'You may proceed when your light is green only when you can do so without endangering or obstructing pedestrians who are still on the crossing.',
      nl: 'U mag pas doorrijden als uw licht groen is wanneer u dat kunt zonder voetgangers die nog op de oversteek zijn te hinderen of in gevaar te brengen.',
      ar: 'يجوز لك المضي عندما يكون ضوءك أخضر فقط عندما تستطيع ذلك دون تعريض المشاة الذين لا يزالون على المعبر للخطر أو عرقلة سيرهم.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-12',
    animationId: null,
    text: {
      en: 'You approach a junction at night. All traffic lights are off. A yellow light flashes on a central bollard. Traffic is approaching from multiple directions. What do you do?',
      nl: 'U nadert een kruising \'s nachts. Alle verkeerslichten zijn uit. Een geel licht knippert op een middenpaaltje. Verkeer nadert van meerdere kanten. Wat doet u?',
      ar: 'تقترب من تقاطع ليلا. جميع إشارات المرور مطفأة. ضوء أصفر يومض على عمود وسطي. مركبات تقترب من عدة اتجاهات. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-12o1',
        text: {
          en: 'Stop completely until the yellow light stops flashing',
          nl: 'Volledig stoppen totdat het gele licht stopt met knipperen',
          ar: 'توقف تماما حتى يتوقف الضوء الأصفر عن الوميض',
        },
      },
      {
        id: 'q-tls2-12o2',
        text: {
          en: 'Proceed with extra caution, applying normal right-of-way rules',
          nl: 'Met extra voorzichtigheid verder rijden, normale voorrangsregels toepassen',
          ar: 'تابع بحذر شديد، مطبقا قواعد الأولوية العادية',
        },
      },
      {
        id: 'q-tls2-12o3',
        text: {
          en: 'Take priority — the yellow means the junction is clear',
          nl: 'Voorrang nemen — het gele licht betekent dat de kruising vrij is',
          ar: 'خذ الأولوية — الأصفر يعني أن التقاطع خال',
        },
      },
    ],
    correctAnswerId: 'q-tls2-12o2',
    explanation: {
      en: 'A flashing yellow light warns of hazard or special conditions — not a clear junction. Proceed carefully and apply normal priority rules; give way to the right on equal roads.',
      nl: 'Een knipperend geel licht waarschuwt voor gevaar of bijzondere omstandigheden — geen vrije kruising. Rijd voorzichtig en pas normale voorrangsregels toe; rechts heeft voorrang.',
      ar: 'الضوء الأصفر الوميض يحذر من خطر أو ظروف خاصة — وليس تقاطعا خاليا. تابع بحذر وطبق قواعد الأولوية العادية؛ الأولوية لليمين على الطرق المتكافئة.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-new-3',
    animationId: null,
    text: {
      en: 'You are on the motorway. The overhead sign above your lane shows a red X. The sign above the lane to your left shows a white arrow pointing left. What do you do?',
      nl: 'U rijdt op de snelweg. Het matrixbord boven uw rijstrook toont een rode X. Het bord boven de rijstrook links van u toont een witte pijl naar links. Wat doet u?',
      ar: 'أنت على الطريق السريع. اللوحة فوق مسارك تظهر علامة X حمراء. اللوحة فوق المسار على يسارك تظهر سهما أبيض يشير إلى اليسار. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls-new-3-o1',
        text: {
          en: 'Continue in your lane — overhead signs on motorways are advisory only',
          nl: 'Doorrijden op uw rijstrook — matrixborden zijn alleen adviserend',
          ar: 'تابع في مسارك — اللوحات العلوية للطريق السريع استشارية فقط',
        },
      },
      {
        id: 'q-tls-new-3-o2',
        text: {
          en: 'Signal and move safely to the left lane — your lane is closed',
          nl: 'Richting aangeven en veilig naar de linkerrijstrook gaan — uw rijstrook is afgesloten',
          ar: 'أشر وانتقل بأمان إلى المسار الأيسر — مسارك مغلق',
        },
      },
      {
        id: 'q-tls-new-3-o3',
        text: {
          en: 'Stop immediately in your lane and switch on hazard lights',
          nl: 'Onmiddellijk op uw rijstrook stoppen en alarmlichten aanzetten',
          ar: 'توقف فورا في مسارك وشغل أضواء الطوارئ',
        },
      },
    ],
    correctAnswerId: 'q-tls-new-3-o2',
    explanation: {
      en: 'A red X means your lane is closed — you must leave it. A white arrow shows the permitted direction. Overhead motorway signs are legally binding, not advisory.',
      nl: 'Een rode X betekent dat uw rijstrook is afgesloten — u moet die verlaten. Een witte pijl toont de toegestane richting. Matrixborden zijn wettelijk verplicht, niet adviserend.',
      ar: 'علامة X الحمراء تعني أن مسارك مغلق — يجب مغادرته. السهم الأبيض يظهر الاتجاه المسموح. اللوحات العلوية للطريق السريع ملزمة قانونا وليست استشارية.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-new-4',
    animationId: null,
    text: {
      en: 'You are driving within a built-up area. A bus at a marked bus stop ahead switches on its right indicator. It has not yet moved. What should you do?',
      nl: 'U rijdt binnen de bebouwde kom. Een bus bij een bushalte voor u zet zijn rechterrichtingaanwijzer aan. Hij is nog niet weggereden. Wat moet u doen?',
      ar: 'أنت تسير داخل منطقة مبنية. حافلة عند محطة حافلات أمامك تشغل مؤشر الانعطاف الأيمن. لم تتحرك بعد. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls-new-4-o1',
        text: {
          en: 'Maintain speed — the bus has not moved yet so you still have priority',
          nl: 'Snelheid handhaven — de bus is nog niet weggereden dus u heeft nog voorrang',
          ar: 'حافظ على السرعة — الحافلة لم تتحرك بعد فلا تزال الأولوية لك',
        },
      },
      {
        id: 'q-tls-new-4-o2',
        text: {
          en: 'Reduce speed and be prepared to give way — the bus has priority when leaving the stop',
          nl: 'Snelheid verminderen en klaarstaan om voor te laten — de bus heeft voorrang bij het verlaten van de halte',
          ar: 'قلل السرعة وكن مستعدا لإعطاء الأولوية — الحافلة لها الأولوية عند مغادرة المحطة',
        },
      },
      {
        id: 'q-tls-new-4-o3',
        text: {
          en: 'Flash your headlights to signal to the bus driver that you are approaching',
          nl: 'Lichten knipperen om de buschauffeur te sein dat u nadert',
          ar: 'وميض الأضواء لإشارة السائق إلى أنك تقترب',
        },
      },
    ],
    correctAnswerId: 'q-tls-new-4-o2',
    explanation: {
      en: 'Under RVV 1990 article 50, within a built-up area you must give way to buses leaving a marked bus stop. As soon as the bus signals its intention to pull out, reduce speed and be prepared to stop.',
      nl: 'Artikel 50 RVV verplicht u binnen de bebouwde kom voorrang te geven aan bussen die een bushalte verlaten. Zodra de bus aangeeft te willen invoegen, vertraag dan en wees bereid te stoppen.',
      ar: 'بموجب المادة 50 من لائحة المرور RVV 1990، يجب إعطاء الأولوية للحافلات التي تغادر محطة محددة داخل المناطق المبنية. بمجرد إشارة الحافلة لنيتها الانطلاق، قلل السرعة وكن مستعدا للتوقف.',
    },
    subject: 'Traffic Lights & Signals',
  },
];
